/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { AutoComplete, Form, Select } from 'antd';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';

// Misc imports
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { GraphContext } from '../../FlowGraph';
import { NODE_TYPES, SWITCH_CASE_CONDITIONS } from '../../../../constants/flowGraph';

// Constant declarations
const { Option } = Select;

const SwitchCaseNodeForm = ({ form, selectedElement }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, botLanguages, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { elements } = useContext(GraphContext);

  // We are within a condition node and we want to show recommendations for available conditions
  // To do that, we need to get the question nodes that are affected (getting that through the value of the flow control node(s) that is attached to the current condition)
  // Then, through the question nodes, we get all options that follow them (= their children) and show their values as recommendations
  var conditionRecommendations = [];

  // Get the parent flow control nodes - via edge connection
  var parentFlowControlNodeIds = elements.filter(element => element.data?.nodeType === 'edge' && element.target === selectedElement.id).map(edge => edge.source);
  var parentFlowControlNodes = elements.filter(element => element.data?.nodeType === 'node' && parentFlowControlNodeIds.some(nodeId => nodeId === element.id));

  // Get the question nodes that are referenced by the parent flow control nodes - via the questionId
  var attachedQuestionNodes = elements.filter(element => parentFlowControlNodes.some(flowNode => `survey.${element.data?.questionId}.answer` === flowNode.data?.switchVariable));

  // Get the options that are attached to the question nodes - via edge connection
  var childOptionNodeIds = elements.filter(element => element.data?.nodeType === 'edge' && attachedQuestionNodes.some(questNode => element.source === questNode.id)).map(edge => edge.target);
  var childOptionNodes = elements.filter(element => element.data?.nodeType === 'node' && childOptionNodeIds.some(nodeId => nodeId === element.id));

  childOptionNodes.forEach(optionNode => {
    // Option nodes can be: Plain options, static multi-options or dynamic multi-options, or text input
    switch (optionNode.type) {
      case NODE_TYPES.OPTION:
        conditionRecommendations.push({ value: optionNode.data?.label[currentBotLanguage] });
        break;

      case NODE_TYPES.MULTI_OPTION:
        if (optionNode.data?.multiOptions[currentBotLanguage]) {
          Object.keys(optionNode.data.multiOptions[currentBotLanguage]).forEach(key => {
            conditionRecommendations.push({ value: key });
          });
        }
        break;

      case NODE_TYPES.DYNAMIC_MULTI_OPTION:
      case NODE_TYPES.TEXT_INPUT:
      default:
        break;
    }
  });

  return (
    <Fragment>
      <Form.Item
        label={t('Condition')}
        name={['data', 'switchCaseData', 'condition']}
        className="mb-2"
      >
        <Select
          placeholder={t('Select a condition')}
          size="large"
          disabled={!isEditingPrimaryLanguage}
          rules={[
            {
              required: true,
              message: t('Select a condition')
            }
          ]}
        >
          {
            Object.entries(SWITCH_CASE_CONDITIONS).map(([key, value]) => (
              <Option key={key} value={value}>{t(value, { context: 'switch case condition' })}</Option>
            ))
          }
        </Select>
      </Form.Item>

      <LabelWithOriginalText label={t('Value')} originalText={form.getFieldValue(['data', 'switchCaseData', 'value', primaryBotLanguage])} />

      {
        botLanguages.map(lang => (
          <Form.Item
            label=""
            name={['data', 'switchCaseData', 'value', lang]}
            hidden={currentBotLanguage !== lang}
            rules={[{ required: lang === primaryBotLanguage && isEditingPrimaryLanguage, message: t('Condition cannot be blank.') }]}
            className="mb-0"
          >
            <AutoComplete
              size="large"
              options={conditionRecommendations}
              placeholder="eg. 45"
            />
          </Form.Item>
        ))
      }
    </Fragment>
  );
};

SwitchCaseNodeForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object.isRequired
};

export default SwitchCaseNodeForm;
